import { Box, Divider, Flex, Spinner, Text } from '@chakra-ui/react';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { fetchOverviewDetails } from 'src/api/queries';
import RecommendationBadge from 'src/components/RecommendationBadge';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import COCRecommendation from 'src/pages/SubmissionDetails/COCRecommendation';
import dateFormat from 'dateformat';
import { DECISION_STATUS_MAP } from 'src/constants';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
interface RecommendationRoundsProps {
  fullWidth?: boolean;
}

const RecommendationRounds: React.FC<RecommendationRoundsProps> = ({
  fullWidth = false
}) => {
  const {
    submissionId,
    workflowResponse: { manuscriptId }
  } = useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const hasCOcEnabled = submissionContext?.config?.isCOCRecommendationRequired;

  const [selectedRound, setSelectedRound] = useState<string>(
    manuscriptId ?? ''
  );

  const {
    data: overviewDetails,
    isFetching,
    isLoading
  } = useQuery({
    queryKey: ['overview-details', submissionId, selectedRound],
    queryFn: () => fetchOverviewDetails(submissionId, selectedRound),
    enabled: Boolean(selectedRound),
    retry: 0,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true
  });

  const revisions = useMemo(
    () => overviewDetails?.overviewRevisions ?? [],
    [overviewDetails]
  );

  const handleAccordionChange = useCallback((manuscriptId: string) => {
    setSelectedRound(prev => (prev === manuscriptId ? '' : manuscriptId));
  }, []);

  const containerWidth = fullWidth ? '100%' : '35%';

  return (
    <>
      {isLoading ? (
        <Flex
          minW={containerWidth}
          maxW={containerWidth}
          justifyContent={'center'}
          alignItems={'start'}
        >
          <Spinner />
        </Flex>
      ) : (
        <>
          {' '}
          {revisions.length >= 1 && (
            <Box minW={containerWidth} maxW={containerWidth}>
              {revisions?.map(revision => {
                const PeerreviewsCount =
                  revision?.overviewDetails?.peerReviewDetails?.participants.filter(
                    participant =>
                      participant?.reviewerRecommendationData?.recommendation
                  ).length;
                const decision =
                  revision?.overviewDetails?.submissionDecisionDetails
                    ?.decision;

                const isEmpty = !decision;

                const decisionLabel = isEmpty
                  ? 'No decision made yet'
                  : decision.type === 'RevisionRequested'
                  ? `${decision.formPayload?.revisionSeverity ?? ''} Revision`
                  : DECISION_STATUS_MAP[decision?.type]?.label;
                return (
                  <Box key={revision?.manuscriptId}>
                    <Accordion
                      chakraAccordionProps={{
                        width: '100%',
                        index:
                          selectedRound === revision?.manuscriptId ? [0] : [-1],
                        background: 'surfaceSecondaryFill',
                        onChange: () =>
                          handleAccordionChange(revision?.manuscriptId)
                      }}
                      accordionItemProps={{
                        border: '1px solid',
                        borderColor: 'neutralPrimaryBorder'
                      }}
                      items={[
                        {
                          title: (
                            <Box>
                              <Text textStyle="headingGroup">
                                {revision?.isOriginalSubmission
                                  ? 'Original Submission'
                                  : `Revision - ${revision?.revisionNumber}`}{' '}
                              </Text>
                              {revision.overviewDetails
                                ?.submissionDecisionDetails?.dateEntered && (
                                <Box>
                                  <Text textStyle="headingItem">
                                    {'('}
                                    {dateFormat(
                                      revision.overviewDetails
                                        ?.submissionDecisionDetails
                                        ?.dateEntered,
                                      'mmm d, yyyy'
                                    )}
                                    {' to '}
                                    {revision?.overviewDetails
                                      ?.submissionDecisionDetails?.dateCompleted
                                      ? dateFormat(
                                          revision.overviewDetails
                                            .submissionDecisionDetails
                                            ?.dateCompleted,
                                          'mmm d, yyyy'
                                        )
                                      : 'Ongoing'}
                                    {')'}
                                  </Text>
                                  <Text>
                                    Decision :{' '}
                                    <Text
                                      as={'span'}
                                      textStyle="textBody"
                                      textTransform={
                                        isEmpty ? 'none' : 'capitalize'
                                      }
                                    >
                                      {decisionLabel}
                                    </Text>
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          ),
                          content:
                            isLoading || isFetching ? (
                              <Flex justifyContent={'center'} my={4}>
                                <FontAwesomeIcon
                                  icon={faSpinner}
                                  spin
                                  size="3x"
                                />
                              </Flex>
                            ) : (
                              <>
                                {hasCOcEnabled && (
                                  <COCRecommendation
                                    round={
                                      revision?.overviewDetails
                                        ?.cocRecommendationDetails
                                    }
                                  />
                                )}
                                {Boolean(PeerreviewsCount) && (
                                  <>
                                    <Box my={4}>
                                      <Text mb={2} textStyle="headingLine">
                                        Peer reviews ({PeerreviewsCount})
                                      </Text>
                                      <Divider />
                                    </Box>
                                    <Box my={2}>
                                      {revision.overviewDetails?.peerReviewDetails?.participants
                                        ?.filter(
                                          participant =>
                                            participant.reviewerRecommendationData
                                        )
                                        .map(participant => {
                                          return (
                                            <Box
                                              border={'1px solid'}
                                              borderStyle="solid"
                                              borderColor="neutralPrimaryBorder"
                                              py={3}
                                              key={participant.reviewId}
                                            >
                                              <Flex gap={3} alignItems="center">
                                                <Text textStyle="headingLine"></Text>
                                                <Text textStyle="headingLine">
                                                  {
                                                    participant.reviewer
                                                      .firstName
                                                  }{' '}
                                                  {
                                                    participant.reviewer
                                                      .lastName
                                                  }
                                                </Text>
                                                {participant.reviewerRecommendationData && (
                                                  <Box>
                                                    <RecommendationBadge
                                                      recommendation={
                                                        participant
                                                          .reviewerRecommendationData
                                                          .recommendation ?? ''
                                                      }
                                                    />
                                                  </Box>
                                                )}

                                                <Text textStyle="textContext">
                                                  {dateFormat(
                                                    participant
                                                      .reviewerRecommendationData
                                                      ?.date,
                                                    'mmm d, yyyy'
                                                  )}
                                                </Text>
                                              </Flex>

                                              <Box mx={3} my={3}>
                                                <Text textStyle="headingItem">
                                                  Comment for Author(s)
                                                </Text>
                                                <Text>
                                                  {participant
                                                    ?.reviewerRecommendationData
                                                    ?.feedbackForAuthors
                                                    .original ?? '--'}
                                                </Text>
                                              </Box>
                                              <Box mx={3} my={3}>
                                                <Text textStyle="headingLine">
                                                  Comment for Staff
                                                </Text>
                                                <Text>
                                                  {participant
                                                    ?.reviewerRecommendationData
                                                    ?.feedbackForEditors ??
                                                    '--'}
                                                </Text>
                                              </Box>
                                            </Box>
                                          );
                                        })}
                                    </Box>
                                  </>
                                )}
                              </>
                            )
                        }
                      ]}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default RecommendationRounds;
